import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';

import { cn } from '@broccoli/ui';

import { errorMessage } from './error-copy';

import { offlineQueue, useOfflineQueueSnapshot } from '@/shared/lib/offline-queue';

import type { OfflineQueueHandle, RejectedWrite } from '@/shared/lib/offline-queue';

/**
 * Spec 08 §8: a persistent, honest status strip. The one rule that matters more than any
 * wording here — a queued report must look queued, not sent. A foreman who believes it went
 * through and finds out on Friday that it did not is worse off than one who never had the
 * queue, so this never says anything that could be mistaken for "delivered" while an item is
 * still only on the phone.
 *
 * It is mounted once, in `AppShell`, and on the login screen. Pending and rejected state is
 * app-wide: a person who queues a report and then taps through to another screen must keep
 * seeing that it has not sent, and the alert carrying a refused report has to be readable from
 * the login screen too — because "your session expired" is exactly when it appears.
 */
const TONE_CLASS: Record<'neutral' | 'warning' | 'success' | 'info' | 'danger', string> = {
  neutral: 'bg-[var(--color-surface-sunken)] text-[var(--color-text-muted)]',
  warning: 'bg-[var(--color-warning-surface)] text-[var(--color-warning)]',
  success: 'bg-[var(--color-success-surface)] text-[var(--color-primary-700)]',
  info: 'bg-[var(--color-info-surface)] text-[var(--color-info)]',
  danger: 'bg-[var(--color-danger-surface)] text-[var(--color-danger)]',
};

/** `/v1/jobs/<id>/site-reports[...]` → the job, so a refused report can be re-opened on it. */
function jobIdOf(path: string): string | null {
  return /^\/v1\/jobs\/([^/]+)\/site-reports/.exec(path)?.[1] ?? null;
}

interface Line {
  label: string;
  value: string;
}

/**
 * What the person typed, in their own language and in full.
 *
 * This used to be `JSON.stringify(body).slice(0, 160)` under a promise that the typed data was
 * still here — so a Thai foreman read six machine keys and an ellipsis, and the problems text,
 * the plan for tomorrow and every task percentage were past the cut. Field names are mapped
 * rather than printed, and nothing is truncated: the panel's whole job is to hand the work
 * back.
 */
function describeBody(body: unknown): Line[] {
  if (!body || typeof body !== 'object') return [];
  const b = body as Record<string, unknown>;
  const lines: Line[] = [];

  const push = (label: string, value: unknown) => {
    if (value === null || value === undefined || value === '') return;
    lines.push({ label, value: String(value) });
  };

  push('วันที่รายงาน', b.reportDate);
  push('วันที่ (นับจากเริ่มงาน)', b.dayNo);
  push('คนเข้างาน', b.headcount == null ? null : `${String(b.headcount)} คน`);
  push(
    'ชั่วโมงทำงาน',
    typeof b.hoursDeci === 'number' ? `${(b.hoursDeci / 10).toFixed(1)} ชม.` : null,
  );
  push('สภาพอากาศ', b.weather);
  push('สภาพหน้างาน', b.siteCondition);
  push('ปัญหา / สิ่งที่ต้องตามต่อ', b.problems);
  push('แผนพรุ่งนี้', b.tomorrowPlan);

  if (Array.isArray(b.tasks) && b.tasks.length > 0) {
    for (const task of b.tasks as { description?: unknown; percentComplete?: unknown }[]) {
      const percent = task?.percentComplete;
      push(
        'งานที่ทำ',
        `${String(task?.description ?? '')}${percent == null ? '' : ` — ${String(percent)}%`}`,
      );
    }
  }

  return lines;
}

function RejectedAlert({ item, onDismiss }: { item: RejectedWrite; onDismiss: () => void }) {
  const jobId = jobIdOf(item.path);
  const lines = describeBody(item.body);

  return (
    <div
      role="alert"
      className={cn('flex flex-col gap-2 rounded-lg px-3 py-2 text-[13px]', TONE_CLASS.danger)}
    >
      <p className="font-medium">
        {errorMessage({ code: item.errorCode, message: item.errorMessage })}
      </p>
      <p className="text-[12px] opacity-80">ข้อมูลที่พิมพ์ไว้ยังอยู่ตรงนี้ ยังไม่หายไปไหน:</p>

      <dl className="flex flex-col gap-0.5 rounded-lg bg-[var(--color-surface)] px-3 py-2 text-[13px] text-[var(--color-text)]">
        {lines.length === 0 ? (
          <p className="text-[12px] text-[var(--color-text-muted)]">ไม่มีรายละเอียดที่บันทึกไว้</p>
        ) : (
          lines.map((line, i) => (
            <div key={`${line.label}-${String(i)}`} className="flex flex-wrap gap-x-2">
              <dt className="text-[12px] text-[var(--color-text-muted)]">{line.label}</dt>
              <dd className="whitespace-pre-wrap">{line.value}</dd>
            </div>
          ))
        )}
      </dl>

      <div className="mt-1 flex flex-wrap items-center gap-2">
        {/* The primary way out is recovery, not acknowledgement: dismissing was the only
            control here, and it deleted the last copy of the report. */}
        {jobId ? (
          <Link
            to={`/site-report?job=${encodeURIComponent(jobId)}&recover=${encodeURIComponent(item.id)}`}
            className="inline-flex min-h-11 items-center rounded-lg bg-[var(--color-danger)] px-3 text-[13px] font-semibold text-white"
          >
            เปิดเพื่อแก้ไขและส่งใหม่
          </Link>
        ) : null}
        <button
          type="button"
          onClick={onDismiss}
          className="inline-flex min-h-11 w-fit items-center rounded-lg border border-current px-3 text-[13px] font-medium"
        >
          ทิ้งรายงานนี้
        </button>
      </div>
    </div>
  );
}

export function OfflineBanner({ queue = offlineQueue }: { queue?: OfflineQueueHandle } = {}) {
  const snapshot = useOfflineQueueSnapshot(queue);
  const [justCleared, setJustCleared] = useState(false);
  const prevRef = useRef({
    pending: snapshot.pendingCount,
    rejectedCount: snapshot.rejected.length,
  });

  useEffect(() => {
    const prev = prevRef.current;
    // "ส่งครบแล้ว" only when the queue emptied itself by actually sending — not because
    // everything in it got dropped as unfixable, which is not the same thing at all.
    const clearedBySending =
      prev.pending > 0 &&
      snapshot.pendingCount === 0 &&
      snapshot.rejected.length === prev.rejectedCount;
    prevRef.current = { pending: snapshot.pendingCount, rejectedCount: snapshot.rejected.length };
    if (!clearedBySending) return;
    setJustCleared(true);
    const timer = setTimeout(() => setJustCleared(false), 4000);
    return () => clearTimeout(timer);
  }, [snapshot.pendingCount, snapshot.rejected.length]);

  let label: string | null = null;
  let tone: keyof typeof TONE_CLASS = 'neutral';
  /* Signing in again is the action that unblocks the queue, so the strip carries it. */
  let signInAgain = false;

  if (snapshot.authBlocked && snapshot.pendingCount > 0) {
    label = `เซสชันหมดอายุ — เข้าสู่ระบบใหม่เพื่อส่ง ${snapshot.pendingCount} รายการที่ค้าง`;
    tone = 'warning';
    signInAgain = true;
  } else if (snapshot.isFlushing) {
    label = 'กำลังส่ง…';
    tone = 'info';
  } else if (snapshot.pendingCount > 0) {
    label = snapshot.isOnline
      ? `${snapshot.pendingCount} รายการรอส่ง`
      : `ออฟไลน์อยู่ — ${snapshot.pendingCount} รายการรอส่ง`;
    tone = 'warning';
  } else if (justCleared) {
    label = 'ส่งครบแล้ว';
    tone = 'success';
  } else if (!snapshot.isOnline) {
    label = 'ออฟไลน์อยู่';
    tone = 'warning';
  }

  const showSendNow =
    snapshot.pendingCount > 0 && snapshot.isOnline && !snapshot.isFlushing && !signInAgain;

  if (!label && snapshot.rejected.length === 0) return null;

  return (
    <div className="mb-3 flex flex-col gap-2">
      {label ? (
        <div
          role="status"
          className={cn(
            'flex min-h-11 flex-wrap items-center justify-between gap-2 rounded-lg px-3 py-2 text-[13px] font-medium',
            TONE_CLASS[tone],
          )}
        >
          <span>{label}</span>
          {signInAgain ? (
            <Link
              to="/login"
              className="inline-flex min-h-11 items-center rounded-lg border border-current px-3 text-[13px] font-semibold"
            >
              เข้าสู่ระบบใหม่
            </Link>
          ) : null}
          {showSendNow ? (
            <button
              type="button"
              onClick={() => void queue.flush()}
              className="min-h-11 rounded-lg border border-current px-3 text-[13px] font-semibold"
            >
              ส่งเดี๋ยวนี้
            </button>
          ) : null}
        </div>
      ) : null}

      {snapshot.rejected.map((item) => (
        <RejectedAlert key={item.id} item={item} onDismiss={() => queue.dismissRejected(item.id)} />
      ))}
    </div>
  );
}
